import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useMutation } from "react-query";
import { useAuthContext } from "../context/AuthContext";
import { useS3Upload } from "../hooks/useS3Upload";
import { generateBannerDraftWithAi } from "../api/client";

const toInt = (v) => (v === "" || v == null ? null : Math.trunc(Number(v)));
const genId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const MAX_FILE_MB = 10;

const base64ToFile = (base64, filename, mimeType = "image/png") => {
  const binary = atob(base64);
  const length = binary.length;
  const bytes = new Uint8Array(length);
  for (let i = 0; i < length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mimeType });
};

// tamanhos recomendados 
const HERO_SIZE_HINT =
  "Banner principal (carousel): 1920 x 600 px (proporção ~16:5).";

const HERO_SIMPLE_SIZE_HINT =
  "Hero simples: 1920 x 600 px (proporção ~16:5), imagem ocupando toda a largura.";

const CARD_SIZE_HINT =
  "Cards: use 4:3 (ex.: 1200 x 900 px) para os cards grandes e 8:3 (ex.: 1200 x 450 px) para os cards horizontais.";

const Preview = ({ banner }) => {
  const isCard = banner?.tipo === "card";
  const isSimpleHero = banner?.tipo === "heroImage"; // novo tipo
  const previewSrc = banner?.imagePreview || banner?.imageSrc;

  const hasOverlayText = !isCard && !isSimpleHero;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 1,
        overflow: "hidden",
        bgcolor:
          banner?.background || (isCard ? "background.default" : "#0b1635"),
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: isCard ? 180 : 260,
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: "#1115",
          cursor: isSimpleHero ? "pointer" : "default",
        }}
      >
        {previewSrc && (
          <Box
            component="img"
            src={previewSrc}
            alt={banner.imageAlt || ""}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              // aqui usamos contain para não cortar o banner
              objectFit: "contain",
              objectPosition: "center",
              opacity: 0.98,
            }}
          />
        )}

        {hasOverlayText && (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              right: 16,
              color: "white",
            }}
          >
            {banner?.eyebrow && (
              <Box
                sx={{
                  display: "inline-flex",
                  px: 1,
                  py: 0.5,
                  fontSize: 12,
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,.18)",
                  backdropFilter: "blur(4px)",
                }}
              >
                {banner.eyebrow}
              </Box>
            )}
            {banner?.title && (
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{
                  mt: 1,
                  textShadow: "0 1px 0 rgba(0,0,0,.2)",
                }}
              >
                {banner.title}
              </Typography>
            )}
            {banner?.subtitle && (
              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                {banner.subtitle}
              </Typography>
            )}
            {banner?.ctaLabel && (
              <Box sx={{ mt: 1.5 }}>
                <Button variant="contained" size="small">
                  {banner.ctaLabel}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {isSimpleHero && !previewSrc && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: 14,
              px: 2,
              textAlign: "center",
            }}
          >
            Banner simples (imagem clicável). Selecione uma imagem e informe o
            link.
          </Box>
        )}
      </Box>
    </Box>
  );
};

const AddBannerForm = ({ bannerData, onClose, onSubmit, isLoading }) => {
  const { userData } = useAuthContext();
  const { uploadOne } = useS3Upload();
  const [errorMessage, setErrorMessage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState(null); // exibe nome do arquivo durante upload
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiBrief, setAiBrief] = useState("");
  const [aiNotice, setAiNotice] = useState("");
  const fileRef = useRef(null);

  // tipo vem do backend ou cai no default "carousel"
  const initialTipo =
    bannerData?.tipo === "card" ||
    bannerData?.tipo === "heroImage" ||
    bannerData?.tipo === "carousel"
      ? bannerData.tipo
      : "carousel";

  const [form, setForm] = useState({
    ...bannerData,
    id_usuario: bannerData?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,
    nome: bannerData?.nome || "",
    tipo: initialTipo, // 'carousel' | 'heroImage' | 'card'
    ordem: bannerData?.ordem ?? "",
    status: bannerData?.status == null ? 1 : Number(bannerData.status),

    eyebrow: bannerData?.eyebrow || "",
    title: bannerData?.title || "",
    subtitle: bannerData?.subtitle || "",
    ctaLabel: bannerData?.ctaLabel || "",
    ctaHref: bannerData?.ctaHref || "",
    imageSrc: bannerData?.imageSrc || "",
    imageAlt: bannerData?.imageAlt || "",
    background: bannerData?.background || "#98999a",

    ratio: bannerData?.ratio || "1175/440", // apenas 'card'
    fit: bannerData?.fit || "cover", // apenas 'card'

    imagePreview: null, // preview local
    imageFile: null, // arquivo selecionado (para upload real)
  });

  useEffect(() => {
    const nextTipo =
      bannerData?.tipo === "card" ||
      bannerData?.tipo === "heroImage" ||
      bannerData?.tipo === "carousel"
        ? bannerData.tipo
        : "carousel";

    setForm((p) => ({
      ...p,
      ...bannerData,
      id_usuario: bannerData?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
      nome: bannerData?.nome || "",
      tipo: nextTipo,
      ordem: bannerData?.ordem ?? "",
      status: bannerData?.status == null ? 1 : Number(bannerData.status),
      eyebrow: bannerData?.eyebrow || "",
      title: bannerData?.title || "",
      subtitle: bannerData?.subtitle || "",
      ctaLabel: bannerData?.ctaLabel || "",
      ctaHref: bannerData?.ctaHref || "",
      imageSrc: bannerData?.imageSrc || "",
      imageAlt: bannerData?.imageAlt || "",
      background: bannerData?.background || "#98999a",
      ratio: bannerData?.ratio || "1175/440",
      fit: bannerData?.fit || "cover",
      imagePreview: null,
      imageFile: null,
    }));
    setAiDialogOpen(false);
    setAiBrief("");
    setAiNotice("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerData?.id]);

  const aiDraftMutation = useMutation(
    async () =>
      generateBannerDraftWithAi({
        brief: aiBrief,
        tipo: form.tipo,
        existing: {
          nome: form.nome,
          eyebrow: form.eyebrow,
          title: form.title,
          subtitle: form.subtitle,
          ctaLabel: form.ctaLabel,
          background: form.background,
        },
      }),
    {
      onSuccess: (data) => {
        const draft = data?.draft || {};
        const image = data?.image || {};
        const nextFile =
          image?.base64 && image?.suggestedFileName
            ? base64ToFile(
                image.base64,
                image.suggestedFileName,
                image.mimeType || "image/png"
              )
            : null;
        const preview = nextFile ? URL.createObjectURL(nextFile) : null;

        setForm((prev) => ({
          ...prev,
          tipo: draft?.tipo || prev.tipo,
          nome: draft?.nome || prev.nome,
          eyebrow: draft?.eyebrow ?? prev.eyebrow,
          title: draft?.title ?? prev.title,
          subtitle: draft?.subtitle ?? prev.subtitle,
          ctaLabel: draft?.ctaLabel ?? prev.ctaLabel,
          imageAlt: draft?.imageAlt || prev.imageAlt,
          background: draft?.background || prev.background,
          imagePreview: preview || prev.imagePreview,
          imageFile: nextFile || prev.imageFile,
        }));
        setAiNotice(
          "A IA preencheu o formulário com um rascunho e aplicou a imagem no preview. Revise tudo antes de salvar."
        );
        setErrorMessage(null);
        setAiDialogOpen(false);
        setAiBrief("");
      },
      onError: (err) => {
        setErrorMessage(
          err?.response?.data?.message ||
            err?.message ||
            "Não foi possível gerar o banner com IA agora."
        );
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAiNotice("");

    // quando troca o tipo, limpamos o que não faz sentido
    if (name === "tipo") {
      const nextTipo = value;
      setForm((p) => ({
        ...p,
        tipo: nextTipo,
        ...(nextTipo === "heroImage"
          ? {
              eyebrow: "",
              title: "",
              subtitle: "",
              ctaLabel: "",
              background: "#0b1635",
            }
          : {}),
      }));
      return;
    }

    setForm((p) => ({ ...p, [name]: value }));
  };

  const pickFile = () => fileRef.current?.click();

  const validateImage = (file) => {
    if (!file) return "Arquivo inválido.";
    const isImage = /^image\//i.test(file.type);
    if (!isImage) return "Selecione uma imagem válida.";
    const sizeOk = file.size <= MAX_FILE_MB * 1024 * 1024;
    if (!sizeOk) return `Imagem maior que ${MAX_FILE_MB}MB.`;
    return null;
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const err = validateImage(f);
    if (err) {
      setErrorMessage(err);
      setTimeout(() => setErrorMessage(null), 4000);
      return;
    }
    setAiNotice("");
    const preview = URL.createObjectURL(f);
    setForm((p) => ({ ...p, imagePreview: preview, imageFile: f }));
  };

  const buildPrefix = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const workspace = userData?.id_workspace || "default";
    return `workspace-${workspace}/banners/${yyyy}/${mm}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!(form.nome || form.title)) {
      setErrorMessage("Informe pelo menos 'Nome' (interno) ou 'Título'.");
      return;
    }

    // Upload real se houver novo arquivo
    let finalImageUrl = form.imageSrc || "";
    try {
      if (form.imageFile) {
        setIsUploading(true);
        setUploadInfo(form.imageFile.name);
        const { url } = await uploadOne(form.imageFile, {
          prefix: buildPrefix(),
        });
        if (!url) throw new Error("Falha ao enviar imagem para o S3.");
        finalImageUrl = url;
      }
    } catch (err) {
      setErrorMessage(err?.message || "Erro ao enviar imagem para o S3.");
      setIsUploading(false);
      setUploadInfo(null);
      return;
    } finally {
      setIsUploading(false);
      setUploadInfo(null);
    }

    const payload = {
      id: form.id,
      nome: (form.nome || form.title || "").trim(),
      tipo: form.tipo, // sempre o valor do select
      ordem: toInt(form.ordem),
      status: Number(form.status) === 1 ? 1 : 0,

      eyebrow: form.tipo === "heroImage" ? "" : form.eyebrow || "",
      title: form.tipo === "heroImage" ? "" : form.title || "",
      subtitle: form.tipo === "heroImage" ? "" : form.subtitle || "",
      ctaLabel:
        form.tipo === "heroImage" ? "" : form.ctaLabel ? form.ctaLabel : "",
      ctaHref: form.ctaHref || "",
      imageSrc: finalImageUrl,
      imageAlt: form.imageAlt || "",
      background:
        form.tipo === "heroImage" ? "" : form.background ? form.background : "",

      ratio: form.ratio || "1175/440",
      fit: form.fit || "cover",

      isNewImage: !!form.imageFile,
      localTmpId: !form.id ? genId() : undefined,

      id_usuario: bannerData?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
    };

    try {
      await onSubmit(payload);
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message || "Erro ao salvar o banner."
      );
    }
  };

  const isCard = form.tipo === "card";
  const isSimpleHero = form.tipo === "heroImage";
  const isGeneratingAi = aiDraftMutation.isLoading;
  const isBusy = isLoading || isUploading || isGeneratingAi;

  let sizeHint = HERO_SIZE_HINT;
  if (isSimpleHero) sizeHint = HERO_SIMPLE_SIZE_HINT;
  if (isCard) sizeHint = CARD_SIZE_HINT;

  return (
    <Paper elevation={3} sx={{ p: 3, width: "95%", m: "0 auto" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="h6">
          {form?.id ? "Editar Banner" : "Adicionar Banner"}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => setAiDialogOpen(true)}
          disabled={isBusy}
        >
          {isGeneratingAi ? "Gerando com IA..." : "Gerar com IA"}
        </Button>
      </Box>

      {(errorMessage || isUploading || aiNotice) && (
        <Box sx={{ mb: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {aiNotice && (
            <Alert severity="success" sx={{ mb: errorMessage ? 1 : 0 }}>
              {aiNotice}
            </Alert>
          )}
          {isUploading && (
            <Alert severity="info">
              {uploadInfo ? `Enviando imagem: ${uploadInfo}` : "Enviando imagem..."}
            </Alert>
          )}
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Coluna esquerda (campos) */}
          <Grid item xs={12} md={7.5}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Nome (interno)"
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Ordem"
                  name="ordem"
                  value={form.ordem ?? ""}
                  onChange={handleChange}
                  inputMode="numeric"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    label="Tipo"
                    name="tipo"
                    value={form.tipo}
                    onChange={handleChange}
                  >
                    <MenuItem value="carousel">
                      Carousel (hero fragmentado)
                    </MenuItem>
                    <MenuItem value="heroImage">
                      Hero simples (imagem clicável)
                    </MenuItem>
                    <MenuItem value="card">Card (grid)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={Number(form.status) === 1}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          status: e.target.checked ? 1 : 0,
                        }))
                      }
                    />
                  }
                  label={Number(form.status) === 1 ? "Ativo" : "Inativo"}
                />
              </Grid>

              {/* Campos de conteúdo só para hero fragmentado (carousel) */}
              {!isCard && !isSimpleHero && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Texto de destaque (acima do título)"
                      name="eyebrow"
                      value={form.eyebrow}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Título"
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Subtítulo"
                      name="subtitle"
                      value={form.subtitle}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                  </Grid>
                </>
              )}

              {/* CTA – no hero simples só usamos o link (sem label/botão) */}
              {!isSimpleHero && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label="TEXTO DO BOTÃO"
                    name="ctaLabel"
                    value={form.ctaLabel}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
              )}
              <Grid item xs={12} md={isSimpleHero ? 12 : 6}>
                <TextField
                  label={
                    isSimpleHero ? "Link do banner (URL completa)" : "LINK DO BOTÃO"
                  }
                  name="ctaHref"
                  value={form.ctaHref}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <TextField
                  label="URL da Imagem"
                  name="imageSrc"
                  value={form.imageSrc}
                  onChange={handleChange}
                  fullWidth
                  placeholder="Cole uma URL ou selecione um arquivo ao lado"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={pickFile}
                  startIcon={<AddPhotoAlternateRoundedIcon />}
                >
                  Selecionar arquivo
                </Button>
                <input
                  ref={fileRef}
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={handleFile}
                />
                {form.imageFile && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 0.5 }}
                    color="text.secondary"
                  >
                    {form.imageFile.name}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Alt (acessibilidade)"
                  name="imageAlt"
                  value={form.imageAlt}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              {/* Cor de fundo só faz sentido para hero fragmentado (com textos) */}
              {!isCard && !isSimpleHero && (
                <Grid item xs={12} md={6}>
                  <TextField
                    type="color"
                    label="Cor de fundo"
                    name="background"
                    value={form.background}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}

              {/* Config específicas de card */}
              {/* {isCard && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Ratio (ex.: 1175/440)"
                      name="ratio"
                      value={form.ratio}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Object Fit</InputLabel>
                      <Select
                        label="Object Fit"
                        name="fit"
                        value={form.fit}
                        onChange={handleChange}
                      >
                        <MenuItem value="cover">cover</MenuItem>
                        <MenuItem value="contain">contain</MenuItem>
                        <MenuItem value="fill">fill</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )} */}
            </Grid>
          </Grid>

          {/* Coluna direita (preview) */}
          <Grid item xs={12} md={4.5}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Preview
            </Typography>
            <Preview banner={form} />
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              As imagens selecionadas fazem <em>upload</em> real para o S3 ao
              salvar. Você também pode colar uma URL pública direta no campo
              &quot;URL da Imagem&quot;.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {sizeHint}
            </Typography>
          </Grid>
        </Grid>

        {/* Ações */}
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isBusy}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isBusy}
          >
            {isBusy ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Salvar"
            )}
          </Button>
        </Box>
      </form>

      <Dialog
        open={aiDialogOpen}
        onClose={() => !isGeneratingAi && setAiDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon fontSize="small" />
          Gerar banner com IA
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Explique o que você quer. A IA vai gerar a imagem e os textos, preencher o formulário e deixar a decisão de salvar com você.
          </Alert>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <Chip
              size="small"
              label={
                form.tipo === "heroImage"
                  ? "Hero simples"
                  : form.tipo === "card"
                  ? "Card"
                  : "Carousel"
              }
              variant="outlined"
              color="primary"
            />
            <Chip
              size="small"
              label="Sem salvar automaticamente"
              variant="outlined"
            />
          </Box>

          <TextField
            label="Descreva o banner desejado"
            placeholder="Ex.: Quero um banner premium para peças de freio, com caminhão em movimento, clima de confiança, CTA curto e visual moderno."
            value={aiBrief}
            onChange={(e) => setAiBrief(e.target.value)}
            fullWidth
            multiline
            minRows={5}
            disabled={isGeneratingAi}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Dica: informe produto, público, clima visual, oferta, estilo e o objetivo do banner. Quanto mais contexto, melhor o resultado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAiDialogOpen(false)}
            disabled={isGeneratingAi}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => aiDraftMutation.mutate()}
            disabled={isGeneratingAi || aiBrief.trim().length < 10}
            startIcon={
              isGeneratingAi ? null : <AutoAwesomeIcon fontSize="small" />
            }
          >
            {isGeneratingAi ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Gerar rascunho"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AddBannerForm;
